import React, { useMemo, useState } from 'react';
import { IconWarning, IconAlert, IconCheck } from '../../../../../components/Icons';
import KSButton from 'presentation/components/KSButton';
import DismissAuditModal from './DismissAuditModal';
import { useDismissBalanceSheetAudit } from 'hooks/reports';
import { AuditFieldResult } from '@hone-automation/common';
import { useLocationsStore } from 'hooks/useLocationsStore';
import { AuditResult } from 'types/report';
import { useNTContext } from 'presentation/components/NewTable/NTContext';
import { showToast, TOAST_ERROR, FIVE_SECONDS } from 'lib/utils';

export interface AuditAlertProps {
  cellId?: string;
  auditResult?: AuditResult;
  accountId?: string;
  locationId?: string;
  className?: string;
  titleIconClassName?: string;
  iconClassName?: string;
  textClassName?: string;
  titleClassName?: string;
  messageClassName?: string;
  buttonClassName?: string;
}

const AuditAlert: React.FC<AuditAlertProps> = ({
  cellId,
  auditResult,
  accountId,
  className = '',
  iconClassName = '',
  textClassName = '',
  titleClassName = '',
  messageClassName = '',
  titleIconClassName = '',
  buttonClassName = '',
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalKey, setModalKey] = useState(0);
  const locationId = useLocationsStore(state => state.currentLocationId);
  const { dismissedAudit, setDismissedAudit } = useNTContext();

  const { mutate: dismissAudit, isPending } = useDismissBalanceSheetAudit();
  const auditResultStateShown = ['warning', 'error', 'fail', 'dismissed'];

  const shouldShow = auditResult && auditResultStateShown.includes(auditResult.type?.toLowerCase() || '');

  if (!shouldShow) {
    return null;
  }

  const auditResultType = useMemo(() => {
    const isDismissedLocally = dismissedAudit.some(
      da => da.rowIndex === Number(accountId) && da.isDismissed === true && da.columnIndex === Number(cellId)
    );
    if (isDismissedLocally) return 'dismissed';
    return auditResult?.type?.toLowerCase();
  }, [auditResult?.type, dismissedAudit, cellId]);

  const getAuditIcon = () => {
    const type = auditResultType;
    if (type === 'dismissed') {
      return <IconCheck />;
    }
    return type === 'warning' ? <IconWarning /> : <IconAlert />;
  };

  const getAuditTitle = () => {
    switch (auditResultType) {
      case 'dismissed':
        return 'Dismissed';
      case 'warning':
        return 'Warning';
      case 'error':
      case 'fail':
        return 'Failure';
    }
  };

  const getAuditMessage = () => {
    return auditResult?.message || "This vendor hasn't been properly configured";
  };

  const handleDismiss = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleConfirmDismiss = (comment: string) => {
    if (!accountId || !locationId || !auditResult) {
      showToast('Missing required data for dismissing audit', TOAST_ERROR, FIVE_SECONDS);
      return;
    }

    // Convert AuditResult to AuditFieldResult format
    const dismissedResult: AuditFieldResult = {
      fieldName: auditResult.fieldName,
      reportName: auditResult.reportName,
      type: auditResult.type,
      reportValue: auditResult.reportValue,
      message: auditResult.message,
    };

    dismissAudit(
      {
        accountId,
        dismissedResult,
        comment,
        locationId,
      },
      {
        onSuccess: data => {
          setIsModalOpen(false);
          setModalKey(prev => prev + 1); // Reset modal state by changing key

          // Find and update existing audit entry instead of adding duplicate
          const existingAuditIndex = dismissedAudit.findIndex(da => da.rowIndex === Number(accountId));

          if (existingAuditIndex !== -1) {
            // Update existing entry
            const updatedDismissedAudit = [...dismissedAudit];
            updatedDismissedAudit[existingAuditIndex] = {
              ...updatedDismissedAudit[existingAuditIndex],
              isDismissed: true,
            };
            setDismissedAudit(updatedDismissedAudit);
          }
        },
      }
    );
  };

  return (
    <>
      <div className={className}>
        <div className={titleIconClassName}>
          <div className={iconClassName}>{getAuditIcon()}</div>
          <div className={textClassName}>
            <div className={titleClassName}>{getAuditTitle()}</div>
            <div className={messageClassName}>{getAuditMessage()}</div>
          </div>
        </div>
        {auditResultType !== 'dismissed' && (
          <div className={buttonClassName}>
            <KSButton variant="contained" size="small" onClick={handleDismiss} label="Dismiss" />
          </div>
        )}
      </div>
      <DismissAuditModal
        key={modalKey}
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onConfirm={handleConfirmDismiss}
        isLoading={isPending}
      />
    </>
  );
};

export default AuditAlert;
